import { Button } from "@/components/ui/button";
import { NumberRequestsAPI } from "@/features/number-requests/api/number-requests";
import {
  useRemoveNumberRequestDocument,
  useUploadNumberRequestDocument,
} from "@/features/number-requests/hooks/use-number-requests";
import type {
  NumberRequestDocument,
  NumberRequestKind,
} from "@/features/number-requests/types";
import { openBlobInTab } from "@/features/utils/blob";
import { useTranslation } from "@/features/utils/i18n";
import { formatFileSize } from "@/features/utils/numbers";
import {
  ArrowSquareOutIcon,
  EyeIcon,
  TrashIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { EditableBlock } from "./editable-block";

/** Scans and photos — what people actually have for these papers. */
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic";

/**
 * One document, as one of the page's read/write blocks.
 *
 * Once the file is in, the only thing worth showing is which file it is — the
 * hint that explains what the operator wants ("both sides, in date, the name
 * the line is registered to") has done its job and is folded away with the rest
 * of the write mode. What stays expanded is what is still missing.
 *
 * Uploading twice replaces (the backend deletes the old object), so there is no
 * "remove then add" dance.
 */
export function DocumentItem({
  clientId,
  requestId,
  kind,
  document,
  address,
  country,
}: {
  clientId: string;
  requestId: string;
  kind: NumberRequestKind;
  document: NumberRequestDocument;
  /** The client's address on file, shown on the proof-of-address line. */
  address?: string | null;
  country?: string | null;
}) {
  const { t } = useTranslation("numberRequests");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpening, setIsOpening] = useState(false);

  const upload = useUploadNumberRequestDocument(clientId);
  const remove = useRemoveNumberRequestDocument(clientId);

  // Per-kind copy where it matters ("pièce d'identité du propriétaire" vs "du
  // titulaire du contrat"), falling back to the label the backend stored so a
  // request created against an older checklist still renders.
  const label = t(`numberRequests.documents.${kind}.${document.type}.label`, {
    defaultValue: document.label,
  });
  const hint = t(`numberRequests.documents.${kind}.${document.type}.hint`, {
    defaultValue: "",
  });

  const handleUpload = async (file?: File | null) => {
    if (!file) return;
    try {
      await upload.mutateAsync({ requestId, type: document.type, file });
      toast.success(t("numberRequests.messages.documentUploaded"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("numberRequests.messages.documentUploadFailed"),
      );
    }
  };

  const handleRemove = async () => {
    try {
      await remove.mutateAsync({ requestId, type: document.type });
      toast.success(t("numberRequests.messages.documentRemoved"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("numberRequests.messages.documentRemoveFailed"),
      );
    }
  };

  // The download route is authenticated, so the file can't just be linked to —
  // it comes back as a blob and is handed to the browser as an object URL.
  const handleOpen = async () => {
    setIsOpening(true);
    try {
      const blob = await NumberRequestsAPI.downloadDocument(
        clientId,
        requestId,
        document.type,
      );
      openBlobInTab(blob);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("numberRequests.messages.documentOpenFailed"),
      );
    } finally {
      setIsOpening(false);
    }
  };

  const isBusy = upload.isPending || remove.isPending;

  const view = document.provided && document.file_key && (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={isOpening}
      onClick={handleOpen}
    >
      <EyeIcon className="h-4 w-4" />
      {t("numberRequests.documents.view")}
    </Button>
  );

  return (
    <EditableBlock
      title={label}
      description={hint || undefined}
      filled={document.provided}
      summary={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-muted-foreground min-w-0 truncate text-sm">
            {document.filename || label}
            {document.size ? ` · ${formatFileSize(document.size)}` : ""}
          </p>
          {view}
        </div>
      }
    >
      {() => (
        <div className="space-y-3">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(event) => {
              handleUpload(event.target.files?.[0]);
              // Reset so re-picking the same file still fires onChange.
              event.target.value = "";
            }}
          />

          {/* The Kbis is free in France and most people pay for it — worth a
              line of its own rather than a footnote. */}
          {document.type === "company_registration" && country === "FR" && (
            <a
              href="https://www.infogreffe.fr"
              target="_blank"
              rel="noreferrer noopener"
              className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
            >
              {t("numberRequests.documents.kbisFree")}
              <ArrowSquareOutIcon className="h-3.5 w-3.5" />
            </a>
          )}

          {/* Showing the address we hold removes the guesswork about *which*
              address the bill has to match. */}
          {document.type === "proof_of_address" && address && (
            <p className="text-muted-foreground text-sm">
              {t("numberRequests.documents.addressToMatch")}{" "}
              <span className="text-foreground font-medium">{address}</span>
            </p>
          )}

          {document.provided && document.filename && (
            <p className="text-muted-foreground truncate text-sm">
              {document.filename}
              {document.size ? ` · ${formatFileSize(document.size)}` : ""}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {view}

            <Button
              type="button"
              variant={document.provided ? "outline" : "default"}
              size="sm"
              disabled={isBusy}
              onClick={() => inputRef.current?.click()}
            >
              <UploadSimpleIcon className="h-4 w-4" />
              {upload.isPending
                ? t("numberRequests.documents.uploading")
                : document.provided
                  ? t("numberRequests.documents.replace")
                  : t("numberRequests.documents.add")}
            </Button>

            {document.provided && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={isBusy}
                onClick={handleRemove}
                aria-label={t("numberRequests.documents.remove")}
              >
                <TrashIcon className="h-4 w-4 text-red-500" />
              </Button>
            )}
          </div>
        </div>
      )}
    </EditableBlock>
  );
}
